import {
  buildNormalizedDay,
  buildNormalizedPilotBlueprint,
  localizedGenderNode,
} from "./referenceNormalizerPilotUtils";

/** Required substring for scripts/validate_dna_blueprints.mjs (runtime writing steps). */
export const DNA_VALIDATE_WRITING_PHRASE = "אני שואל/ת את עצמי בשקט";

const days = [
  {
    dayNumber: 1,
    emotionalStage: "pause",
    title: "Noticing Comparison in Real Time",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I don’t fight comparison. I simply notice it when it appears."
    ],
      [
      "Daily intention: Today I don’t fight comparison. I simply notice it when it appears."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One slow breath.",
      "In my heart: “This is comparison. Not truth.”",
      "I notice where it lives in my body — without fixing it. Just noticing."
    ],
      [
      "I pause with myself.",
      "One slow breath.",
      "In my heart: “This is comparison. Not truth.”",
      "I notice where it lives in my body — without fixing it. Just noticing."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "1. When did comparison show up today? (place/person/social media)\n[Write here]",
      "2. What exactly did I compare? (appearance/money/success/relationship/peace)\n[Write here]",
      "3. What did the comparison say about me? (exact sentence)\n[Write here]"
    ],
      [
      "1. When did comparison show up today? (place/person/social media)\n[Write here]",
      "2. What exactly did I compare? (appearance/money/success/relationship/peace)\n[Write here]",
      "3. What did the comparison say about me? (exact sentence)\n[Write here]"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing:",
      "Today I don’t believe every sentence that appears. I just see it."
    ],
      [
      "Closing:",
      "Today I don’t believe every sentence that appears. I just see it."
    ]
    ),
    action: localizedGenderNode(
      "Once today: one breath + “Coming back to myself.”",
      "Once today: one breath + “Coming back to myself.”"
    ),
  },
  {
    dayNumber: 2,
    emotionalStage: "pain",
    title: "Fact vs. Interpretation (Gentle CBT)",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I separate what happened from the story my mind tells."
    ],
      [
      "Daily intention: Today I separate what happened from the story my mind tells."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “A fact doesn’t have to hurt like this.”",
      "I watch the thought like a cloud passing."
    ],
      [
      "One breath. “A fact doesn’t have to hurt like this.”",
      "I watch the thought like a cloud passing."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "1. What is the bare fact I saw? (no interpretation)\n[Write here]",
      "2. What interpretation jumped in? (“It means I’m…”)\n[Write here]",
      "3. What is one gentler alternative interpretation?\n[Write here]",
      "4. What happens if I stay only with the fact?\n[Write here]"
    ],
      [
      "1. What is the bare fact I saw? (no interpretation)\n[Write here]",
      "2. What interpretation jumped in? (“It means I’m…”)\n[Write here]",
      "3. What is one gentler alternative interpretation?\n[Write here]",
      "4. What happens if I stay only with the fact?\n[Write here]"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing:",
      "My pain is real — the story isn’t always accurate."
    ],
      [
      "Closing:",
      "My pain is real — the story isn’t always accurate."
    ]
    ),
    action: localizedGenderNode(
      "Once today: “Fact ≠ interpretation” + breath.",
      "Once today: “Fact ≠ interpretation” + breath."
    ),
  },
  {
    dayNumber: 3,
    emotionalStage: "pain",
    title: "My Main Triggers",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I learn where comparison loves to appear."
    ],
      [
      "Daily intention: Today I learn where comparison loves to appear."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “Knowing the trigger gives me power.”",
      "I notice heartbeat/chest/belly — no rush."
    ],
      [
      "One breath. “Knowing the trigger gives me power.”",
      "I notice heartbeat/chest/belly — no rush."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "1. My 3 main comparison triggers:\n[Write here]",
      "2. What emotion is under the comparison? (shame/fear/insecurity/loneliness)\n[Write here]",
      "3. What do I usually do right after the trigger? (shrink/compare more/avoid/try to impress)\n[Write here]"
    ],
      [
      "1. My 3 main comparison triggers:\n[Write here]",
      "2. What emotion is under the comparison? (shame/fear/insecurity/loneliness)\n[Write here]",
      "3. What do I usually do right after the trigger? (shrink/compare more/avoid/try to impress)\n[Write here]"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing:",
      "I’m not broken. I have a pattern."
    ],
      [
      "Closing:",
      "I’m not broken. I have a pattern."
    ]
    ),
    action: localizedGenderNode(
      "Today I choose one trigger and practice a 10-second pause.",
      "Today I choose one trigger and practice a 10-second pause."
    ),
  },
  {
    dayNumber: 4,
    emotionalStage: "pattern",
    title: "Comparison as Protection",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I see comparison as a crooked attempt to protect me."
    ],
      [
      "Daily intention: Today I see comparison as a crooked attempt to protect me."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “Comparison tries to create control.”",
      "I ask: what am I trying not to feel?"
    ],
      [
      "One breath. “Comparison tries to create control.”",
      "I ask: what am I trying not to feel?"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "1. What is comparison trying to protect me from feeling?\n[Write here]",
      "2. What do I fear will happen if I’m “behind”?\n[Write here]",
      "3. Which part of me needs safety right now?\n[Write here]"
    ],
      [
      "1. What is comparison trying to protect me from feeling?\n[Write here]",
      "2. What do I fear will happen if I’m “behind”?\n[Write here]",
      "3. Which part of me needs safety right now?\n[Write here]"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing:",
      "Understanding calms the mechanism."
    ],
      [
      "Closing:",
      "Understanding calms the mechanism."
    ]
    ),
    action: localizedGenderNode(
      "One sentence: “I see why you do this. Thank you.” + breath.",
      "One sentence: “I see why you do this. Thank you.” + breath."
    ),
  },
  {
    dayNumber: 5,
    emotionalStage: "pattern",
    title: "Bringing Worth Back Home",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I bring measurement back to myself — not to others."
    ],
      [
      "Daily intention: Today I bring measurement back to myself — not to others."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “My life is not a competition.”",
      "I notice one good thing in me right now — even small."
    ],
      [
      "One breath. “My life is not a competition.”",
      "I notice one good thing in me right now — even small."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "1. In which area do I feel “less”?\n[Write here]",
      "2. What is true about me that isn’t measured there?\n[Write here]",
      "3. One value I choose to measure myself by (truth/respect/peace/growth)\n[Write here]"
    ],
      [
      "1. In which area do I feel “less”?\n[Write here]",
      "2. What is true about me that isn’t measured there?\n[Write here]",
      "3. One value I choose to measure myself by (truth/respect/peace/growth)\n[Write here]"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing:",
      "Self-worth isn’t built by comparison. It’s built inside."
    ],
      [
      "Closing:",
      "Self-worth isn’t built by comparison. It’s built inside."
    ]
    ),
    action: localizedGenderNode(
      "I write 3 qualities that are uniquely mine.",
      "I write 3 qualities that are uniquely mine."
    ),
  },
  {
    dayNumber: 6,
    emotionalStage: "fear",
    title: "Social Media: Gentle Cleaning",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I reduce exposure that fuels comparison."
    ],
      [
      "Daily intention: Today I reduce exposure that fuels comparison."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “I don’t have to step into fire.”",
      "Before opening an app — one breath and ask: “Is this good for me now?”"
    ],
      [
      "One breath. “I don’t have to step into fire.”",
      "Before opening an app — one breath and ask: “Is this good for me now?”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "1. What type of content triggers me most?\n[Write here]",
      "2. What am I really seeking there? (approval/hope/escape/inspiration?)\n[Write here]",
      "3. One small boundary for this week\n[Write here]"
    ],
      [
      "1. What type of content triggers me most?\n[Write here]",
      "2. What am I really seeking there? (approval/hope/escape/inspiration?)\n[Write here]",
      "3. One small boundary for this week\n[Write here]"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing:",
      "Boundaries are self-respect."
    ],
      [
      "Closing:",
      "Boundaries are self-respect."
    ]
    ),
    action: localizedGenderNode(
      "Today I mute/remove one trigger.",
      "Today I mute/remove one trigger."
    ),
  },
  {
    dayNumber: 7,
    emotionalStage: "need",
    title: "Turning Comparison into Information",
    opening: localizedGenderNode(
      [
      "Daily intention: If I compare — maybe it shows me what I want."
    ],
      [
      "Daily intention: If I compare — maybe it shows me what I want."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “Comparison can become a compass.”",
      "I notice the sting and ask: what is it pointing to?"
    ],
      [
      "One breath. “Comparison can become a compass.”",
      "I notice the sting and ask: what is it pointing to?"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "1. Who/what did I compare myself to today?\n[Write here]",
      "2. What does it reveal I actually want?\n[Write here]",
      "3. What is a 10% step in that direction?\n[Write here]"
    ],
      [
      "1. Who/what did I compare myself to today?\n[Write here]",
      "2. What does it reveal I actually want?\n[Write here]",
      "3. What is a 10% step in that direction?\n[Write here]"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing:",
      "I stop punishing myself and start listening."
    ],
      [
      "Closing:",
      "I stop punishing myself and start listening."
    ]
    ),
    action: localizedGenderNode(
      "One 10% step only.",
      "One 10% step only."
    ),
  },
  {
    dayNumber: 8,
    emotionalStage: "softening",
    title: "Gentle Exposure (ACT)",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I allow comparison to be here for one minute — without acting."
    ],
      [
      "Daily intention: Today I allow comparison to be here for one minute — without acting."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “It’s a wave.”",
      "I let the sensation exist — just breathing."
    ],
      [
      "One breath. “It’s a wave.”",
      "I let the sensation exist — just breathing."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "1. What triggered it today?\n[Write here]",
      "2. What happened in my body after one minute?\n[Write here]",
      "3. What did I learn about the wave?\n[Write here]"
    ],
      [
      "1. What triggered it today?\n[Write here]",
      "2. What happened in my body after one minute?\n[Write here]",
      "3. What did I learn about the wave?\n[Write here]"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing:",
      "Strength grows by staying."
    ],
      [
      "Closing:",
      "Strength grows by staying."
    ]
    ),
    action: localizedGenderNode(
      "One minute only. Consistency > force.",
      "One minute only. Consistency > force."
    ),
  },
  {
    dayNumber: 9,
    emotionalStage: "softening",
    title: "Shame Beneath Comparison",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I touch shame without letting it crush me."
    ],
      [
      "Daily intention: Today I touch shame without letting it crush me."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “Shame is pain, not truth.”",
      "Hand on chest/belly (if comfortable), one breath."
    ],
      [
      "One breath. “Shame is pain, not truth.”",
      "Hand on chest/belly (if comfortable), one breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "1. What is the shaming sentence?\n[Write here]",
      "2. What would I say to a loved friend with this sentence?\n[Write here]",
      "3. One gentle sentence I can say to myself today\n[Write here]"
    ],
      [
      "1. What is the shaming sentence?\n[Write here]",
      "2. What would I say to a loved friend with this sentence?\n[Write here]",
      "3. One gentle sentence I can say to myself today\n[Write here]"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing:",
      "Compassion heals more than proof."
    ],
      [
      "Closing:",
      "Compassion heals more than proof."
    ]
    ),
    action: localizedGenderNode(
      "One gentle sentence, once.",
      "One gentle sentence, once."
    ),
  },
  {
    dayNumber: 10,
    emotionalStage: "perspective",
    title: "No More Winning to Be Worthy",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I see the effort to be ‘more’ and soften it."
    ],
      [
      "Daily intention: Today I see the effort to be ‘more’ and soften it."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “I don’t have to win to be loved.”",
      "I notice where I shrink or perform to impress."
    ],
      [
      "One breath. “I don’t have to win to be loved.”",
      "I notice where I shrink or perform to impress."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "1. Where do I try to prove my worth?\n[Write here]",
      "2. What is the cost?\n[Write here]",
      "3. What does “enough” look like today?\n[Write here]"
    ],
      [
      "1. Where do I try to prove my worth?\n[Write here]",
      "2. What is the cost?\n[Write here]",
      "3. What does “enough” look like today?\n[Write here]"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing:",
      "I am worthy even when I’m simply me."
    ],
      [
      "Closing:",
      "I am worthy even when I’m simply me."
    ]
    ),
    action: localizedGenderNode(
      "One action today without explaining or proving.",
      "One action today without explaining or proving."
    ),
  },
  {
    dayNumber: 11,
    emotionalStage: "strength",
    title: "Comparison Inside Relationships",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I notice comparison inside connection."
    ],
      [
      "Daily intention: Today I notice comparison inside connection."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “I don’t have to be ‘more’ to stay.”",
      "I feel the urge to be perfect and breathe once."
    ],
      [
      "One breath. “I don’t have to be ‘more’ to stay.”",
      "I feel the urge to be perfect and breathe once."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "1. Who do I compare myself to in relationships?\n[Write here]",
      "2. What do I fear will happen if I’m ‘less’?\n[Write here]",
      "3. One small inner boundary that helps me\n[Write here]"
    ],
      [
      "1. Who do I compare myself to in relationships?\n[Write here]",
      "2. What do I fear will happen if I’m ‘less’?\n[Write here]",
      "3. One small inner boundary that helps me\n[Write here]"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing:",
      "Real connection doesn’t require competition."
    ],
      [
      "Closing:",
      "Real connection doesn’t require competition."
    ]
    ),
    action: localizedGenderNode(
      "Once today: “I stay myself.” + breath.",
      "Once today: “I stay myself.” + breath."
    ),
  },
  {
    dayNumber: 12,
    emotionalStage: "release",
    title: "Changing the Inner Language",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I change one sentence that runs my life."
    ],
      [
      "Daily intention: Today I change one sentence that runs my life."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “Words are a home.”",
      "I listen to my inner words like music — tone can change."
    ],
      [
      "One breath. “Words are a home.”",
      "I listen to my inner words like music — tone can change."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "1. My main comparison sentence\n[Write here]",
      "2. A humane, non-violent version of it\n[Write here]",
      "3. One new sentence I adopt for the week\n[Write here]"
    ],
      [
      "1. My main comparison sentence\n[Write here]",
      "2. A humane, non-violent version of it\n[Write here]",
      "3. One new sentence I adopt for the week\n[Write here]"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing:",
      "Change a word — change direction."
    ],
      [
      "Closing:",
      "Change a word — change direction."
    ]
    ),
    action: localizedGenderNode(
      "Use the new sentence once today.",
      "Use the new sentence once today."
    ),
  },
  {
    dayNumber: 13,
    emotionalStage: "choice",
    title: "Building Identity Without Measurement",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I build an identity of movement, not comparison."
    ],
      [
      "Daily intention: Today I build an identity of movement, not comparison."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “I’m on my way.”",
      "What truly matters to me, independent of others?"
    ],
      [
      "One breath. “I’m on my way.”",
      "What truly matters to me, independent of others?"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "1. One value I choose to lead me\n[Write here]",
      "2. One small habit serving that value this week\n[Write here]",
      "3. One promise to myself on hard comparison days\n[Write here]"
    ],
      [
      "1. One value I choose to lead me\n[Write here]",
      "2. One small habit serving that value this week\n[Write here]",
      "3. One promise to myself on hard comparison days\n[Write here]"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing:",
      "I am not a competition. I am a journey."
    ],
      [
      "Closing:",
      "I am not a competition. I am a journey."
    ]
    ),
    action: localizedGenderNode(
      "Plan one small stabilizing action for the week.",
      "Plan one small stabilizing action for the week."
    ),
  },
  {
    dayNumber: 14,
    emotionalStage: "continuation",
    title: "Gentle Closure: Coming Home",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I close without winning. I come home."
    ],
      [
      "Daily intention: Today I close without winning. I come home."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “I’m on my side.”",
      "I feel the ground beneath me. I’m here."
    ],
      [
      "One breath. “I’m on my side.”",
      "I feel the ground beneath me. I’m here."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "1. What shifted in me during this journey? (3 things)\n[Write here]",
      "2. What is clearer about comparison now?\n[Write here]",
      "3. A return sentence for hard days (short, pocket-size)\n[Write here]"
    ],
      [
      "1. What shifted in me during this journey? (3 things)\n[Write here]",
      "2. What is clearer about comparison now?\n[Write here]",
      "3. A return sentence for hard days (short, pocket-size)\n[Write here]"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Final closure:",
      "I don’t erase comparison.",
      "I simply stop letting it run my life.",
      "I keep walking —",
      "slowly, quietly, with myself.",
      "And that is strength."
    ],
      [
      "Final closure:",
      "I don’t erase comparison.",
      "I simply stop letting it run my life.",
      "I keep walking —",
      "slowly, quietly, with myself.",
      "And that is strength."
    ]
    ),
    action: localizedGenderNode(
      "One action that says “I’m back with myself.”",
      "One action that says “I’m back with myself.”"
    ),
  },
].map(buildNormalizedDay);

export const constantComparisonToOthersBlueprintEn = buildNormalizedPilotBlueprint({
  id: "constant_comparison_to_others_14d",
  version: "tri_locale_emit_en_v1",
  durationDays: 14,
  problemKey: "constant_comparison_to_others",
  category: "general",
  journeyName: {
    he: "השוואה תמידית לאחרים",
    en: "Constant Comparison to Others",
    es: "Comparación constante con los demás",
  },
  days,
});

export default constantComparisonToOthersBlueprintEn;
